/* eslint-disable sonarjs/cognitive-complexity */
import Link from 'next/link';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext } from 'react';
import { BILLING_ADDRESS_ONLY_POSTCODE } from '../../brands/default/content/components/address';
import {
  AUTO_ADDRESS_LOOKUP,
  EIR_POSTCODE,
  TEXT_LOWERCASE,
} from '../../brands/default/features';
import { trackEvent } from '../../helpers/analytics';
import { validatePostCode } from '../../helpers/validations';
import ThemeContext from '../../providers/ThemeProvider';
import Message from '../_generic/Message';
import FormikInput from '../_generic/input/FormikInput';
import AddressFields from './AddressFields';
import LookupVerifyPostcode from './LookupVerifyPostcode';

const BillingAddressOnlyPostcode = ({
  disabled = false,
  errors = {},
  extraClasses = '',
  isRegistrationPage = false,
  isShowError = false,
  onBlurFunction,
  onChangeFunction,
  onManualAddress = () => {},
  showAllFields = false,
  touched = {},
  values = {},
}) => {
  const theme = useContext(ThemeContext);
  const [showAddressFields, setShowAddressFields] = useState(showAllFields);
  const { lookupOrEnterAddress, registrationEirCode } =
    BILLING_ADDRESS_ONLY_POSTCODE;

  const classes = `mobile-highlight-border ${extraClasses} ${
    TEXT_LOWERCASE && 'lowercase_placeholder'
  }`;

  useEffect(() => {
    if (!AUTO_ADDRESS_LOOKUP) {
      setShowAddressFields(true);
      onManualAddress(true);
    }
  }, [onManualAddress]);

  const handleBlur = (event) => {
    const name = event.target.name;
    if (name === 'addressLine1') trackEvent('address_line1_field');
    if (name === 'city') trackEvent('address_city_field');
    if (name === 'addressLine2') trackEvent('address_line2_field');

    if (name === 'postCode') {
      if (EIR_POSTCODE) {
        trackEvent('eircode_field');
      } else {
        trackEvent('postcode_field');
      }
    }

    onBlurFunction(event);
  };

  // postcode lookup is NOT valid, postcode regex is valid
  const showUsePostcode =
    errors.postCode && Boolean(validatePostCode(values.postCode));

  const manualAddress = (event) => {
    // keyboard - only trigger with enter key
    if (event && event.key && event.key !== 'Enter') return;

    event.preventDefault();
    onManualAddress(true, values.postCode, values);
  };

  return (
    <>
      {AUTO_ADDRESS_LOOKUP && !showAddressFields && (
        <LookupVerifyPostcode
          disabled={disabled}
          errors={errors}
          extraClasses={classes}
          onBlurFunction={handleBlur}
          onChangeFunction={onChangeFunction}
          touched={touched}
          values={values}
        />
      )}
      {isShowError && !showAddressFields && (
        <div className="col-12">
          <Message
            message={lookupOrEnterAddress}
            showIcon={false}
            type="ERROR"
          />
        </div>
      )}
      {AUTO_ADDRESS_LOOKUP && !showAddressFields && showUsePostcode && (
        <div className="col-12 input-container text-right">
          <span>Is this postcode correct? </span>
          <Link href="#">
            <a
              className="manual-address"
              onClick={manualAddress}
              onKeyDown={manualAddress}
              role="link"
              tabIndex={0}
            >
              Yes, use this postcode
            </a>
          </Link>
        </div>
      )}
      {showAddressFields && (
        <AddressFields
          componentClass={classes}
          disabled={disabled}
          errors={errors}
          isShowPostcode={AUTO_ADDRESS_LOOKUP}
          onBlurFunction={handleBlur}
          onChangeFunction={onChangeFunction}
          touched={touched}
          values={values}
        />
      )}
      {!AUTO_ADDRESS_LOOKUP && showAddressFields && (
        <div className="col-12">
          <FormikInput
            disabled={disabled}
            extraClasses={classes}
            inputTouched={touched.postCode}
            inputValue={values.postCode}
            name={'postCode'}
            onBlurFunction={handleBlur}
            onChangeFunction={onChangeFunction}
            placeholder={`${registrationEirCode}${
              isRegistrationPage ? '' : '*'
            }`}
            required={!isRegistrationPage}
            type="text"
          />
        </div>
      )}
      <style jsx>{`
        a.manual-address {
          color: ${theme.colors.primary};
        }
      `}</style>
    </>
  );
};

BillingAddressOnlyPostcode.propTypes = {
  disabled: PropTypes.bool,
  errors: PropTypes.object.isRequired,
  extraClasses: PropTypes.string,
  isRegistrationPage: PropTypes.bool,
  isShowError: PropTypes.bool,
  onBlurFunction: PropTypes.func.isRequired,
  onChangeFunction: PropTypes.func.isRequired,
  onManualAddress: PropTypes.func,
  showAllFields: PropTypes.bool,
  touched: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
};

export default BillingAddressOnlyPostcode;
